import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { keyframes } from '@mui/material/styles';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { ChipV2, dsV2 } from '@bloomays-lib/ui.shared';
import type { IContact } from '@bloomays-lib/types.shared';
import type {
  MissionAlert,
  ContactGroupSource,
  ContactAlarm,
  ContactSlotKey,
} from '../../../helpers/missionDashboardV2';

/** Titre Instrument Serif (toolbar, hero, popins). */
export const SerifTitle = ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
  <Box
    component="span"
    sx={{
      fontFamily: dsV2.font.serif,
      fontWeight: 400,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      color: dsV2.color.noir,
      ...sx,
    }}
  >
    {children}
  </Box>
);

/** En-tête de section (créa `.section-label`) : libellé Instrument + trait de séparation à droite. */
export const SectionLabel = ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '22px', marginBottom: '10px', ...sx }}>
    <SerifTitle sx={{ fontSize: 17, whiteSpace: 'nowrap' }}>{children}</SerifTitle>
    <Box sx={{ flex: 1, height: '1px', background: dsV2.color.borderLight }} />
  </Box>
);

/** Légende des sources de contact (créa `.legend-bar`) : badges mission / fallback + note. */
export const ContactsLegend = () => {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '11.5px',
    fontWeight: 600,
  } as const;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
      <Box component="span" sx={{ ...base, background: dsV2.color.bleuDim, color: dsV2.color.bleu }}>
        <AssignmentRoundedIcon sx={{ fontSize: 13 }} /> Contact mission
      </Box>
      <Box
        component="span"
        sx={{
          ...base,
          background: dsV2.color.amberDim,
          color: dsV2.color.amber,
          border: `1px dashed ${dsV2.color.amberBrd}`,
        }}
      >
        <SyncRoundedIcon sx={{ fontSize: 13 }} /> Fallback société
      </Box>
      <Box sx={{ fontSize: 11, color: dsV2.color.t4, fontStyle: 'italic' }}>
        Les contacts mission sont prioritaires ; à défaut, les contacts de la fiche société s'appliquent.
      </Box>
    </Box>
  );
};

/** Skeleton de section (chargement progressif) — surface arrondie de la charte V2. */
export const Skel = ({ height = 160 }: { height?: number }) => (
  <Skeleton
    variant="rounded"
    width="100%"
    height={height}
    sx={{ borderRadius: dsV2.radius.lg, bgcolor: dsV2.color.surface2 }}
  />
);

export type CardTone = 'bleu' | 'vert' | 'violet' | 'beige' | 'neutral';

const cardIconTone = (tone: CardTone): { bg: string; fg: string } => {
  switch (tone) {
    case 'bleu':
      return { bg: dsV2.color.bleuDim, fg: dsV2.color.bleu };
    case 'vert':
      return { bg: dsV2.color.vertDim, fg: dsV2.color.vert };
    case 'violet':
      return { bg: dsV2.color.violetDim, fg: dsV2.color.violet };
    case 'beige':
      return { bg: dsV2.color.beigeDim, fg: dsV2.color.t2 };
    case 'neutral':
    default:
      return { bg: dsV2.color.surface2, fg: dsV2.color.t2 };
  }
};

/** Carte DS V2 (`.card` + `.card-header` + `.card-body`). Titre = Poppins uppercase 11px. */
export const Card = ({
  icon,
  iconTone = 'neutral',
  title,
  action,
  children,
  bodySx,
  id,
}: {
  icon?: React.ReactNode;
  iconTone?: CardTone;
  title?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  bodySx?: object;
  id?: string;
}) => {
  const tint = cardIconTone(iconTone);
  return (
    <Box
      id={id}
      component="section"
      sx={{
        backgroundColor: dsV2.color.blanc,
        border: `1px solid ${dsV2.color.border}`,
        borderRadius: dsV2.radius.lg,
        boxShadow: dsV2.shadow.sm,
        overflow: 'hidden',
        transition: 'box-shadow .2s',
        '&:hover': { boxShadow: dsV2.shadow.hover },
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            padding: '13px 18px 11px',
            borderBottom: `1px solid ${dsV2.color.borderLight}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            {icon ? (
              <Box
                sx={{
                  width: 27,
                  height: 27,
                  borderRadius: '7px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: `1px solid ${dsV2.color.borderLight}`,
                  backgroundColor: tint.bg,
                  color: tint.fg,
                }}
              >
                {icon}
              </Box>
            ) : null}
            <Box
              sx={{
                fontFamily: dsV2.font.sansBold,
                fontWeight: 700,
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: dsV2.color.noir,
              }}
            >
              {title}
            </Box>
          </Box>
          {action}
        </Box>
      )}
      <Box sx={{ padding: '16px 18px', ...bodySx }}>{children}</Box>
    </Box>
  );
};

/** Bouton « fantôme » bleu doux (`.btn-ghost`) — actions d'en-tête de carte. */
export const GhostButton = ({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      background: dsV2.color.bleuDouxLight,
      color: dsV2.color.bleu,
      border: '1px solid transparent',
      borderRadius: dsV2.radius.xs,
      fontFamily: dsV2.font.sansSemiBold,
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background .15s ease',
      '&:hover': { background: dsV2.color.bleuDoux },
    }}
  >
    {children}
  </Box>
);

export type KpiAccent = 'vert' | 'bleu' | 'noir' | 'solaire' | '';

/** Carte KPI (`.kpi`) avec barre d'accent colorée en pied. */
export const KpiCard = ({
  label,
  value,
  sub,
  accent = '',
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  accent?: KpiAccent;
}) => {
  const barColor =
    accent === 'vert'
      ? dsV2.color.vert
      : accent === 'bleu'
      ? dsV2.color.bleu
      : accent === 'noir'
      ? dsV2.color.noir
      : accent === 'solaire'
      ? dsV2.color.solaire
      : dsV2.color.border;
  const valueColor =
    accent === 'vert'
      ? dsV2.color.vert
      : accent === 'bleu'
      ? dsV2.color.bleu
      : accent === 'solaire'
      ? dsV2.color.solaireText
      : dsV2.color.noir;
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: dsV2.color.blanc,
        border: `1px solid ${dsV2.color.border}`,
        borderRadius: dsV2.radius.lg,
        boxShadow: dsV2.shadow.sm,
        padding: '15px 17px 18px',
        transition: 'box-shadow .2s',
        '&:hover': { boxShadow: dsV2.shadow.hover },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 14,
          right: 14,
          height: 3,
          borderRadius: '3px 3px 0 0',
          backgroundColor: barColor,
        },
      }}
    >
      <Box
        sx={{
          fontSize: 10,
          fontWeight: 600,
          color: dsV2.color.t4,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: '6px',
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          fontFamily: dsV2.font.sansBold,
          fontVariantNumeric: 'tabular-nums',
          fontSize: '1.35rem',
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: '-0.02em',
          marginBottom: '3px',
          color: valueColor,
        }}
      >
        {value}
      </Box>
      {sub ? <Box sx={{ fontSize: '10.5px', color: dsV2.color.t4 }}>{sub}</Box> : null}
    </Box>
  );
};

/** Ligne libellé / valeur en lecture (`.f-row`). */
export const FieldRow = ({
  label,
  value,
  last,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  last?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: '12px',
      padding: last ? '8px 0 0' : '8px 0',
      borderBottom: last ? 'none' : `1px solid ${dsV2.color.borderLight}`,
      '&:first-of-type': { paddingTop: 0 },
    }}
  >
    <Box sx={{ fontSize: '11.5px', color: dsV2.color.t3, flexShrink: 0, whiteSpace: 'nowrap' }}>{label}</Box>
    <Box
      sx={{ fontSize: '12.5px', fontWeight: 500, color: dsV2.color.noir, textAlign: 'right', wordBreak: 'break-word' }}
    >
      {value ?? '—'}
    </Box>
  </Box>
);

const blink = keyframes`0%,100%{opacity:1}50%{opacity:.3}`;

/** Pastille statut (`.pill`) : vert clignotant si actif, gris sinon. */
export const StatusPill = ({ active }: { active: boolean }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      padding: '3px 10px',
      borderRadius: '100px',
      fontSize: '10.5px',
      fontWeight: 600,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      ...(active
        ? { background: dsV2.color.vertDim, color: dsV2.color.vert, border: `1px solid ${dsV2.color.vertBrd}` }
        : { background: dsV2.color.surface2, color: dsV2.color.t3, border: `1px solid ${dsV2.color.border}` }),
    }}
  >
    {active ? (
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: dsV2.color.vert,
          animation: `${blink} 2s ease infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    ) : null}
    {active ? 'En cours' : 'Terminée'}
  </Box>
);

/** Tag type de mission (`.tag-it`) — bleu plein. */
export const TypeTag = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 10px',
      borderRadius: dsV2.radius.xs,
      background: dsV2.color.bleu,
      color: '#fff',
      fontSize: '10.5px',
      fontWeight: 600,
      letterSpacing: '0.04em',
    }}
  >
    {children}
  </Box>
);

/** Badge de source d'un groupe de contacts (mission prioritaire / société fallback). */
export const SrcBadge = ({ source }: { source: ContactGroupSource }) => {
  if (source === 'society') return <ChipV2 label="Société" tone="warning" size="small" />;
  if (source === 'mission') return <ChipV2 label="Mission" tone="info" size="small" />;
  return <ChipV2 label="Aucun" tone="neutral" size="small" />;
};

const AVA_TINTS = [dsV2.color.noir, '#5B5ED4', dsV2.color.bleu, '#B03080', dsV2.color.vert, '#7A7E1A'];
const tintOf = (seed = '') => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVA_TINTS[h % AVA_TINTS.length];
};
const initialsOf = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

/** Action d'icône d'une ligne contact (écrire / appeler en lien natif, modifier en bouton). */
const ContactRowAction = ({
  title,
  href,
  onClick,
  children,
}: {
  title: string;
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) => {
  const sharedSx = {
    width: 26,
    height: 26,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '7px',
    color: dsV2.color.t3,
    cursor: 'pointer',
    border: `1px solid ${dsV2.color.border}`,
    background: dsV2.color.blanc,
    transition: 'color .15s, border-color .15s, background .15s',
    '&:hover': { color: dsV2.color.bleu, borderColor: dsV2.color.bleu, background: dsV2.color.bleuDim },
  };
  if (href) {
    return (
      <Box
        component="a"
        href={href}
        title={title}
        aria-label={title}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        sx={{ ...sharedSx, textDecoration: 'none' }}
      >
        {children}
      </Box>
    );
  }
  return (
    <Box component="button" type="button" title={title} aria-label={title} onClick={onClick} sx={sharedSx}>
      {children}
    </Box>
  );
};

/** Ligne contact (`.ce`) : avatar à teinte déterministe, nom, fonction, email + actions au survol. */
export const ContactRow = ({ contact, onEdit }: { contact: IContact; onEdit?: () => void }) => {
  const name = contact.fullname || `${contact.firstname ?? ''} ${contact.lastname ?? ''}`.trim() || 'Contact';
  const hasActions = Boolean(contact.email || contact.phoneNumber || onEdit);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        padding: '10px 0',
        '&:hover .ce-actions': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: '9px',
          background: tintOf(name),
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: dsV2.font.sansSemiBold,
          fontWeight: 600,
          fontSize: 34 * 0.32,
          letterSpacing: '0.02em',
        }}
      >
        {initialsOf(name)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: '12.5px', fontWeight: 600, lineHeight: 1.3, color: dsV2.color.noir }}>{name}</Box>
        {contact.nameJob ? (
          <Box
            sx={{
              fontSize: 11,
              color: dsV2.color.t4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {contact.nameJob}
          </Box>
        ) : null}
        {contact.email ? (
          <Box
            sx={{
              fontSize: 11,
              color: dsV2.color.bleu,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {contact.email}
          </Box>
        ) : null}
      </Box>
      {hasActions ? (
        <Box
          className="ce-actions"
          sx={{
            display: 'flex',
            gap: '4px',
            flexShrink: 0,
            opacity: { xs: 1, md: 0 },
            transition: 'opacity .15s',
          }}
        >
          {contact.email ? (
            <ContactRowAction title="Écrire" href={`mailto:${contact.email}`}>
              <MailOutlineRoundedIcon sx={{ fontSize: 14 }} />
            </ContactRowAction>
          ) : null}
          {contact.phoneNumber ? (
            <ContactRowAction title="Appeler" href={`tel:${contact.phoneNumber}`}>
              <CallRoundedIcon sx={{ fontSize: 14 }} />
            </ContactRowAction>
          ) : null}
          {onEdit ? (
            <ContactRowAction title="Modifier" onClick={onEdit}>
              <EditRoundedIcon sx={{ fontSize: 14 }} />
            </ContactRowAction>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

/** Sous-bloc Bloomer (la personne freelance) — propre au panneau Fournisseur (créa `BloomerRow`). */
export const BloomerRow = ({
  name,
  subtitle,
  email,
  phoneNumber,
  onEdit,
}: {
  name: string;
  subtitle?: string;
  email?: string;
  phoneNumber?: string;
  onEdit?: () => void;
}) => (
  <Box sx={{ borderBottom: `1px solid ${dsV2.color.borderLight}` }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 18px',
        background: dsV2.color.surface2,
        borderBottom: `1px solid ${dsV2.color.borderLight}`,
      }}
    >
      <Box
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: dsV2.color.t3,
        }}
      >
        Bloomer · Principal
      </Box>
      <Box sx={{ marginLeft: 'auto' }}>
        <SrcBadge source="mission" />
      </Box>
    </Box>
    <Box sx={{ padding: '0 18px', '&:hover .ce-actions': { opacity: 1 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 0' }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: '9px',
            background: tintOf(name),
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: dsV2.font.sansSemiBold,
            fontWeight: 600,
            fontSize: 34 * 0.32,
          }}
        >
          {initialsOf(name)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '12.5px', fontWeight: 600, lineHeight: 1.3, color: dsV2.color.noir }}>{name}</Box>
          {subtitle ? <Box sx={{ fontSize: 11, color: dsV2.color.t4 }}>{subtitle}</Box> : null}
          {email ? (
            <Box
              sx={{
                fontSize: 11,
                color: dsV2.color.bleu,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {email}
              {phoneNumber ? ` · ${phoneNumber}` : ''}
            </Box>
          ) : null}
        </Box>
        <Box
          className="ce-actions"
          sx={{ display: 'flex', gap: '4px', flexShrink: 0, opacity: { xs: 1, md: 0 }, transition: 'opacity .15s' }}
        >
          {email ? (
            <ContactRowAction title="Écrire" href={`mailto:${email}`}>
              <MailOutlineRoundedIcon sx={{ fontSize: 14 }} />
            </ContactRowAction>
          ) : null}
          {phoneNumber ? (
            <ContactRowAction title="Appeler" href={`tel:${phoneNumber}`}>
              <CallRoundedIcon sx={{ fontSize: 14 }} />
            </ContactRowAction>
          ) : null}
          {onEdit ? (
            <ContactRowAction title="Modifier le Bloomer" onClick={onEdit}>
              <EditRoundedIcon sx={{ fontSize: 14 }} />
            </ContactRowAction>
          ) : null}
        </Box>
      </Box>
    </Box>
  </Box>
);

/** Grille calendaire CRA — composant autonome (testé via story). Réexporté ici pour compat. */
export { CraMonthGrid, CraMonthGrid as CraCalendar } from './CraMonthGrid';

export type Verbatim = { from: 'client' | 'bloomer'; text: string; author: string; date?: string };

/** Slider de verbatims satisfaction (`.vb-slider`) : badge source + flèches/points + citation. */
export const SatisfactionComments = ({ comments }: { comments: Verbatim[] }) => {
  const [index, setIndex] = React.useState(0);
  if (!comments || comments.length === 0) return null;
  const idx = Math.min(index, comments.length - 1);
  const current = comments[idx];
  const isClient = current.from === 'client';
  const go = (delta: number) => setIndex((x) => (((x + delta) % comments.length) + comments.length) % comments.length);

  return (
    <Box sx={{ marginTop: '12px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          marginBottom: '8px',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '3px 9px',
            borderRadius: '100px',
            background: isClient ? dsV2.color.violetDim : dsV2.color.bleuDim,
            color: isClient ? dsV2.color.violet : dsV2.color.bleu,
          }}
        >
          {isClient ? <ApartmentRoundedIcon sx={{ fontSize: 12 }} /> : <PersonRoundedIcon sx={{ fontSize: 12 }} />}
          {isClient ? 'Client' : 'Bloomer'}
        </Box>
        {comments.length > 1 ? (
          <Stack direction="row" spacing="8px" alignItems="center">
            <VbArrow onClick={() => go(-1)} ariaLabel="Précédent">
              <ChevronLeftRoundedIcon sx={{ fontSize: 16 }} />
            </VbArrow>
            <Stack direction="row" spacing="5px">
              {comments.map((_, k) => (
                <Box
                  key={k}
                  onClick={() => setIndex(k)}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    background: k === idx ? dsV2.color.bleu : dsV2.color.border,
                  }}
                />
              ))}
            </Stack>
            <VbArrow onClick={() => go(1)} ariaLabel="Suivant">
              <ChevronRightRoundedIcon sx={{ fontSize: 16 }} />
            </VbArrow>
          </Stack>
        ) : null}
      </Box>
      <Box
        sx={{
          padding: '11px 14px',
          background: dsV2.color.surface2,
          borderRadius: dsV2.radius.sm,
          border: `1px solid ${dsV2.color.border}`,
          borderLeft: `3px solid ${dsV2.color.bleu}`,
        }}
      >
        <Box
          component="p"
          sx={{ fontSize: 12, color: dsV2.color.t2, fontStyle: 'italic', lineHeight: 1.6, marginBottom: '5px' }}
        >
          “{current.text}”
        </Box>
        <Box component="cite" sx={{ fontSize: 11, color: dsV2.color.t4, fontStyle: 'normal' }}>
          — {current.author}
          {current.date ? ` · ${current.date}` : ''}
        </Box>
      </Box>
    </Box>
  );
};

const VbArrow = ({
  children,
  onClick,
  ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
}) => (
  <Box
    component="button"
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    sx={{
      width: 24,
      height: 24,
      borderRadius: '6px',
      border: `1px solid ${dsV2.color.border}`,
      background: dsV2.color.blanc,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: dsV2.color.t3,
      transition: 'all .12s',
      '&:hover': { background: dsV2.color.surface2, color: dsV2.color.noir },
    }}
  >
    {children}
  </Box>
);

/** Bandeau d'alertes documentaires (`.alert.warn` / `.alert.danger`). */
export const AlertsBanner = ({
  alerts,
  onRegularize,
}: {
  alerts: MissionAlert[];
  onRegularize?: (kind: string) => void;
}) => {
  if (alerts.length === 0) return null;
  return (
    <Stack spacing="8px" sx={{ marginBottom: '14px' }}>
      {alerts.map((alert) => {
        // Tons (retour design) : manquant / signature urgente (mission démarrée) → rouge (prioritaire) ;
        // expiré → jaune ; signature en cours → bleu.
        const tone =
          alert.status === 'missing' || (alert.status === 'signing' && alert.urgent)
            ? 'danger'
            : alert.status === 'signing'
            ? 'info'
            : 'warning';
        const palette =
          tone === 'danger'
            ? { background: dsV2.color.rougeDim, color: '#CC1500', border: `1px solid ${dsV2.color.rougeBrd}` }
            : tone === 'info'
            ? { background: dsV2.color.bleuDim, color: dsV2.color.bleuDark, border: `1px solid ${dsV2.color.bleuMid}` }
            : { background: dsV2.color.amberDim, color: '#8A5500', border: `1px solid ${dsV2.color.amberBrd}` };
        const message =
          alert.status === 'missing'
            ? `${alert.name} — ${alert.kind === 'contract' ? 'Contrat manquant' : 'Document non uploadé'}`
            : alert.status === 'signing'
            ? `${alert.name} — Signature en cours${alert.urgent ? ' · mission démarrée' : ''}`
            : `${alert.name} — Expiré${
                alert.expirationDate ? ` le ${new Date(alert.expirationDate).toLocaleDateString('fr-FR')}` : ''
              }`;
        return (
          <Box
            key={`${alert.kind}-${alert.name}-${alert.status}`}
            role="alert"
            onClick={() => onRegularize?.(alert.kind)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              padding: '10px 14px',
              borderRadius: dsV2.radius.sm,
              fontSize: 12,
              fontWeight: 500,
              cursor: onRegularize ? 'pointer' : 'default',
              transition: 'filter .15s',
              '&:hover': { filter: 'brightness(0.98)' },
              ...palette,
            }}
          >
            {tone === 'danger' ? (
              <ErrorOutlineRoundedIcon sx={{ fontSize: 16 }} />
            ) : tone === 'info' ? (
              <SyncRoundedIcon sx={{ fontSize: 16 }} />
            ) : (
              <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
            )}
            <span>{message}</span>
            {onRegularize ? (
              <Box
                component="span"
                sx={{
                  marginLeft: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                Régulariser <ChevronRightRoundedIcon sx={{ fontSize: 15 }} />
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Stack>
  );
};

const ALARM_SIDE_LABEL: Record<'client' | 'supplier', string> = { client: 'Client', supplier: 'Fournisseur' };

/**
 * Bandeau d'alarmes de non-conformité des contacts (§8, décision spec #4) : NON bloquant — signale
 * les rôles obligatoires (min 1) sans aucun contact, avec un raccourci « Ajouter ». Vide si conforme.
 */
export const ContactAlarmsBanner = ({
  alarms,
  onFix,
}: {
  alarms: ContactAlarm[];
  onFix?: (key: ContactSlotKey) => void;
}) => {
  if (alarms.length === 0) return null;
  return (
    <Stack spacing="8px" sx={{ marginBottom: '12px' }}>
      {alarms.map((alarm) => (
        <Box
          key={alarm.key}
          role="alert"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            padding: '10px 14px',
            borderRadius: dsV2.radius.sm,
            fontSize: 12,
            fontWeight: 500,
            background: dsV2.color.amberDim,
            color: '#8A5500',
            border: `1px solid ${dsV2.color.amberBrd}`,
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
          <span>
            {ALARM_SIDE_LABEL[alarm.side]} · {alarm.label} — aucun contact (rôle obligatoire)
          </span>
          {onFix ? (
            <Box
              component="button"
              type="button"
              onClick={() => onFix(alarm.key)}
              sx={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                border: 'none',
                background: 'transparent',
                color: 'inherit',
                fontSize: 12,
              }}
            >
              Ajouter <ChevronRightRoundedIcon sx={{ fontSize: 15 }} />
            </Box>
          ) : null}
        </Box>
      ))}
    </Stack>
  );
};
